import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, Apple, CreditCard, Banknote, BellRing } from "lucide-react";
import { useSettings } from "../hooks/useSettings";
import { venmoPayHref, venmoProfileHref, firstName } from "../lib/venmo";
import { setPendingPlan, takePendingPlan } from "../lib/pendingPlan";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const METHOD_LABELS = { venmo: "Venmo", zelle: "Zelle", applepay: "Apple Pay", cash: "Cash" };

const PLANS = [
  {
    key: "dropin", name: "Drop-In", price: "$20", unit: "single class",
    perks: ["One 45-min mat class", "Reserve any open mat", "Credit valid 30 days"],
  },
  {
    key: "mini_sculpt", name: "Mini Sculpt", price: "$50", unit: "4 classes",
    perks: ["4 mat classes", "$12.50 per class", "Credits valid 30 days"],
  },
  {
    key: "power_sculpt", name: "Power Sculpt", price: "$100", unit: "8 classes",
    perks: ["8 mat classes", "$12.50 per class", "Credits valid 30 days"], popular: true,
  },
  {
    key: "hot_classes", name: "Hot Classes", price: "$80", unit: "per month",
    perks: ["Unlimited hot classes", "Fridays, Saturdays & Sundays", "Heated room — bring a towel"], hot: true,
  },
  {
    key: "vip_unlimited", name: "VIP Unlimited", price: "$140", unit: "per month",
    perks: ["Unlimited mat classes", "Top waitlist priority for full classes & mats", "Priority class crashing — book full classes", "Book any class this month, no credits needed"], vip: true,
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export const Pricing = () => {
  const STUDIO = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);
  const [altOpen, setAltOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [payerName, setPayerName] = useState("");
  const [method, setMethod] = useState("venmo");
  const [claiming, setClaiming] = useState(false);
  const cardsOn = !!STUDIO.card_payments_enabled;

  const telHref = (p) => `tel:+1${(p || "").replace(/\D/g, "")}`;
  const smsHref = (p) => `sms:+1${(p || "").replace(/\D/g, "")}`;
  const venmoNote = plan ? `${plan.name}${firstName(user) ? ` – ${firstName(user)}` : ""}` : "";

  const checkout = (key) => {
    const selected = PLANS.find((p) => p.key === key) || null;
    setPlan(selected);
    setClaimed(false);
    setPayerName(user?.name || "");
    setAltOpen(true);
  };

  const submitClaim = async (opts = {}) => {
    const m = opts.method || method;
    const name = payerName.trim() || (user?.name || "").trim();
    if (!name) { toast.error("Please enter your name so the studio can match your payment."); return; }
    setClaiming(true);
    try {
      await api.post("/payments/claim", { package: plan.key, method: m, payer_name: name, opened_link: !!opts.openedLink });
      setClaimed(true);
      toast.success(opts.openedLink
        ? `The studio has been told to look for your ${METHOD_LABELS[m]} payment.`
        : "The studio has been notified of your request.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setClaiming(false); }
  };

  // Tapping Venmo / Zelle / Apple Pay leaves the app, so file the (still pending) claim right then —
  // otherwise clients pay and never come back to press "Notify the studio".
  const payLinkTapped = (m) => {
    setMethod(m);
    if (user && plan && !claimed && !claiming) submitClaim({ method: m, openedLink: true });
  };

  // Came back from sign-in mid-purchase? Re-open the package they picked.
  useEffect(() => {
    if (!user) return;
    const pending = takePendingPlan();
    if (!pending) return;
    const selected = PLANS.find((p) => p.key === pending) || null;
    setPlan(selected); setClaimed(false); setPayerName(user?.name || ""); setAltOpen(true);
    toast.success("Welcome back — your package is ready to pay for.");
  }, [user]);

  const startCardCheckout = async (key) => {
    if (!user) {
      setPendingPlan(key);
      setAltOpen(false);
      toast("Sign in or create your account first so we can add this to it.", { duration: 6000 });
      navigate("/login");
      return;
    }
    setLoadingKey(key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start card checkout. Please try again.", { duration: 9000 });
      setLoadingKey(null);
    }
  };

  return (
    <section id="pricing" data-testid="pricing" className="relative bg-black py-24 sm:py-32 border-t border-neutral-900">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-16 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.35em] text-neon mb-5">Pricing</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Pick your <span className="font-script normal-case neon-text">rhythm</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.key}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              data-testid={`pricing-card-${p.key}`}
              className={`relative flex flex-col p-8 bg-[#0A0A0A] border ${
                p.vip ? "border-neon neon-glow" : "border-neutral-800"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-8 bg-white text-black text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                  Most Popular
                </span>
              )}
              {p.vip && (
                <span className="absolute -top-3 left-8 bg-neon text-black font-semibold text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                  Best Value
                </span>
              )}
              <h3 className="font-display uppercase text-2xl mb-1">{p.name}</h3>
              <div className="flex items-end gap-2 mb-6">
                <span className="font-display text-5xl neon-text">{p.price}</span>
                <span className="text-xs text-neutral-400 mb-2 uppercase tracking-wider">{p.unit}</span>
              </div>
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm text-neutral-400">
                    <Check size={16} className="text-neon shrink-0 mt-0.5" /> {perk}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => checkout(p.key)}
                disabled={loadingKey === p.key}
                data-testid={`pricing-btn-${p.key}`}
                className={`inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-4 transition-colors duration-200 ${
                  p.vip
                    ? "bg-neon text-black font-semibold hover:bg-white hover:text-black"
                    : "border border-neutral-700 hover:border-neon hover:text-neon"
                } disabled:opacity-60`}
              >
                {loadingKey === p.key ? <Loader2 size={16} className="animate-spin" /> : null}
                {p.vip ? "Start Membership" : "Book Now"}
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 border border-neutral-900 bg-[#0A0A0A] p-7"
        >
          <div className="flex items-center gap-8">
            <span className="text-xs uppercase tracking-[0.25em] text-neutral-400">We accept</span>
            <div className="flex items-center gap-5 text-neutral-300">
              <span className="flex items-center gap-1.5 text-sm"><Apple size={17} /> Apple Pay</span>
              {cardsOn && <span className="flex items-center gap-1.5 text-sm" data-testid="accept-cards"><CreditCard size={17} /> Visa · Mastercard · Amex · Discover</span>}
              <span className="text-sm">Zelle</span>
              <span className="text-sm">Venmo</span>
              <span className="text-sm">Cash</span>
            </div>
          </div>
          <button
            onClick={() => { setPlan(null); setAltOpen(true); }}
            data-testid="alt-pay-btn"
            className="text-xs uppercase tracking-[0.2em] text-neon hover:text-white transition-colors"
          >
            {cardsOn ? "Pay with Card / Venmo / Zelle / Apple Pay →" : "Pay with Venmo / Zelle / Apple Pay / Cash →"}
          </button>
        </motion.div>
      </div>

      <Dialog open={altOpen} onOpenChange={setAltOpen}>
        <DialogContent data-testid="alt-pay-dialog" className="bg-[#0A0A0A] border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-3xl">
              {plan ? `${plan.name} — ${plan.price}` : "Venmo, Zelle & Apple Pay"}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {plan
                ? cardsOn
                  ? `Pay ${plan.price} by card for instant ${plan.vip ? "membership" : "credits"}, or send it direct and we'll confirm by text.`
                  : `Send ${plan.price} using any option below and we'll confirm your ${plan.name} ${plan.vip ? "membership" : "credits"} by text.`
                : "Send your package total using any option below and we'll confirm your credits by text."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {cardsOn && (
              <div data-testid="card-pay-section" className="border border-neon bg-neon/5 p-5 neon-glow">
                <p className="text-xs uppercase tracking-[0.2em] text-neon mb-3 flex items-center gap-1.5">
                  <CreditCard size={13} /> Pay by card — instant confirmation
                </p>
                {!user && (
                  <p data-testid="card-signin-note" className="text-xs text-neutral-400 mb-3">
                    Sign in or create a free account first — card purchases are added straight to your account.
                  </p>
                )}
                {plan ? (
                  <button onClick={() => startCardCheckout(plan.key)} disabled={loadingKey === plan.key}
                    data-testid="pay-card-btn"
                    className="w-full flex items-center justify-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
                    <CreditCard size={15} /> {loadingKey === plan.key ? "Opening secure checkout…" : `Pay ${plan.price} with card`}
                  </button>
                ) : (
                  <div data-testid="pay-card-picker" className="grid grid-cols-2 gap-2">
                    {PLANS.map((p) => (
                      <button key={p.key} onClick={() => startCardCheckout(p.key)} disabled={loadingKey === p.key}
                        data-testid={`pay-card-${p.key}`}
                        className="border border-neon/40 text-neutral-200 text-xs uppercase tracking-wider py-3 px-2 hover:bg-neon hover:text-black transition-colors disabled:opacity-60">
                        {loadingKey === p.key ? "Opening…" : `${p.name} · ${p.price}`}
                      </button>
                    ))}
                  </div>
                )}
                <p data-testid="card-brands-note" className="mt-3 text-[11px] uppercase tracking-[0.18em] text-neutral-300">
                  Visa · Mastercard · Amex · Discover · Apple Pay · Google Pay
                </p>
                <p data-testid="save-card-note" className="mt-1.5 text-xs text-neutral-400 leading-relaxed">
                  Secure checkout by Stripe — your card details never touch our servers. Credits or membership are added the moment the payment goes through.
                </p>
                {STUDIO.card_payments_mode === "test" && (
                  <p data-testid="card-test-mode-badge" className="mt-2 inline-block text-[10px] uppercase tracking-[0.2em] text-amber-400 border border-amber-400/50 px-2 py-1">
                    Test mode — real cards will be declined
                  </p>
                )}
              </div>
            )}
            {cardsOn && <p className="text-center text-[11px] uppercase tracking-[0.25em] text-neutral-400">or pay direct — Venmo · Zelle · Apple Cash · Cash</p>}
            {plan && (
              <div className="border border-neon/40 bg-neon/5 p-5" data-testid="claim-section">
                {claimed ? (
                  <p data-testid="claim-success" className="text-sm text-neutral-200 flex items-start gap-2">
                    <Check size={16} className="text-neon shrink-0 mt-0.5" />
                    Studio notified ({METHOD_LABELS[method] || "payment"}) — finish sending your payment and your {plan.vip || plan.hot ? "membership" : "credits"} will be added as soon as the studio confirms it arrived.
                  </p>
                ) : user ? (
                  <div data-testid="claim-form">
                    <p className="text-sm text-neutral-300 mb-3 flex items-start gap-2">
                      <BellRing size={15} className="text-neon shrink-0 mt-0.5" />
                      Tapping Venmo, Zelle or Apple Pay below tells the studio automatically. Paying cash? Tell us here so we can match your {plan.name} payment.
                    </p>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5" htmlFor="claim-name">Your name (as it appears on Venmo / Zelle)</label>
                    <input id="claim-name" value={payerName} onChange={(e) => setPayerName(e.target.value)} maxLength={80}
                      data-testid="claim-name-input" placeholder="First and last name"
                      className="w-full bg-black border border-neutral-700 focus:border-neon outline-none text-white text-sm px-3 py-3 mb-3" />
                    <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5">How are you paying?</p>
                    <div className="grid grid-cols-4 gap-1.5 mb-4" role="radiogroup" aria-label="Payment method">
                      {[["venmo", "Venmo"], ["zelle", "Zelle"], ["applepay", "Apple Pay"], ["cash", "Cash"]].map(([k, label]) => (
                        <button key={k} type="button" role="radio" aria-checked={method === k} onClick={() => setMethod(k)}
                          data-testid={`claim-method-${k}`}
                          className={`text-[11px] uppercase tracking-wider py-2.5 border transition-colors ${method === k ? "border-neon bg-neon text-black font-semibold" : "border-neutral-700 text-neutral-300 hover:border-neon"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => submitClaim()} disabled={claiming} data-testid="claim-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] py-3.5 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
                      {claiming ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Notify the studio
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-neutral-400 mb-3 flex items-start gap-2">
                      <BellRing size={15} className="text-neon shrink-0 mt-0.5" />
                      Sign in and tap Book Now again — the studio gets notified instantly and adds your {plan.vip || plan.hot ? "membership" : "credits"} once payment is confirmed.
                    </p>
                    <button onClick={() => { setAltOpen(false); navigate("/login"); }} data-testid="claim-signin-btn"
                      className="w-full border border-neon/50 text-neon text-xs uppercase tracking-[0.2em] py-3 hover:bg-neon hover:text-black transition-colors">
                      Sign in to request {plan.name}
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="border border-neutral-800 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-neon mb-2">Venmo</p>
              <a href={plan ? venmoPayHref(STUDIO.venmo, plan.price, venmoNote) : venmoProfileHref(STUDIO.venmo)} target="_blank" rel="noreferrer" data-testid="pay-venmo"
                onClick={() => payLinkTapped("venmo")}
                className="text-lg underline decoration-neon/50 underline-offset-4 hover:text-neon transition-colors">{STUDIO.venmo}</a>
              {plan ? (
                <a href={venmoPayHref(STUDIO.venmo, plan.price, venmoNote)} target="_blank" rel="noreferrer" data-testid="pay-venmo-prefilled"
                  onClick={() => payLinkTapped("venmo")}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-neon/60 text-neon text-xs uppercase tracking-[0.2em] py-3 hover:bg-neon hover:text-black transition-colors">
                  Open Venmo with {plan.price} + note filled in
                </a>
              ) : null}
              <p className="text-sm text-neutral-400 mt-2" data-testid="pay-venmo-phone">
                <a href={telHref(STUDIO.phone)} className="hover:text-neon transition-colors">{STUDIO.phone}</a> — linked Venmo account{plan ? ` · note will read “${venmoNote}”` : " · tap the handle to open Venmo"}
              </p>
            </div>
            <div className="border border-neutral-800 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-neon mb-2">Zelle</p>
              <a href={telHref(STUDIO.zelle)} data-testid="pay-zelle" onClick={() => payLinkTapped("zelle")}
                className="text-lg underline decoration-neon/50 underline-offset-4 hover:text-neon transition-colors">{STUDIO.zelle}</a>
              <p className="text-sm text-neutral-400 mt-1">
                <a href={`mailto:${STUDIO.email}`} className="hover:text-neon transition-colors">{STUDIO.email}</a>
              </p>
            </div>
            <div className="border border-neutral-800 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-neon mb-2 flex items-center gap-1.5"><Apple size={13} /> Apple Pay</p>
              <a href={smsHref(STUDIO.phone)} data-testid="pay-applepay" onClick={() => payLinkTapped("applepay")}
                className="text-lg underline decoration-neon/50 underline-offset-4 hover:text-neon transition-colors">{STUDIO.phone}</a>
              <p className="text-sm text-neutral-400 mt-1">Tap to open Messages — send via Apple Cash to this number</p>
            </div>
            <div className="border border-neutral-800 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-neon mb-2 flex items-center gap-1.5"><Banknote size={13} /> Cash</p>
              <p className="text-lg" data-testid="pay-cash">Accepted at the studio</p>
              <p className="text-sm text-neutral-400">Pay in person before class — we'll add your credits on the spot</p>
            </div>
            <p className="text-xs text-neutral-400">
              Include your name and the package (e.g. "{plan ? plan.name : "Power Sculpt"}") in the note.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
